/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {Conditional} from "../../Conditional";

import {service as ConfirmationStatusService} from "../../../Services/ConfirmationStatusService";
import {service as EnvironmentalBenefitService} from "../../../Services/EnvironmentalBenefitService";

import {EnvironmentalBenefitFilter} from "../EnvironmentalBenefitFilter";
import {ContextNodeBenefitSummary} from "../ContextNodeBenefitSummary";
import {usePrimeCache} from "../../../Hooks/ServiceHooks";

import {selectCurrentUser} from "../../../Redux/Selectors/userSelectors";

import {useBenefitValues, useFilteredBenefits} from "../../../Hooks/BenefitHooks";

/**
 * We may need:
 * ErrorDispatch={{
 * 				ShowAlert: setAlert,
 * 				SetError: setError,
 * 				DefaultHandler: defaultErrorHandler,
 * 				GetStackTrace: getStackTrace,
 * 				ShowPermissionDenied: showPermissionDenied
 * 			}}
 * 		
 * 	And state: publicContext: userState.user?.publicContext,	
 */

export const ContextNodeBenefitSummarySection = (props) => {

    const { contextNodeId, editMode } = props;
    
    const [benefitSummary, reloadBenefits] = useBenefitValues(contextNodeId);
    const [selectedEnvironmentalBenefitId, setSelectedEnvironmentalBenefitId] = useState(null);
    
    const loaded = usePrimeCache([ConfirmationStatusService, EnvironmentalBenefitService]);
    
    const publicContext = useSelector(selectCurrentUser)?.publicContext
    
    const confirmationStatuses = ConfirmationStatusService.CachedValues()?.result;
    const environmentalBenefits = EnvironmentalBenefitService.CachedValues()?.result;
    
    // clear the benefit summary if editMode = true
    // so that when the user returns to the view mode
    // we will reload it. 
    // TODO: we should probably see if
    // we can only perform a reload when the user
    // saves their data for the contextNodeId instead.
    // IDEA: use redux to dispatch "ContextNode/C.A. changed"
    // events that we can also listen with a selector
    useEffect(() => {
        if(!editMode) {
            reloadBenefits();
        }
    }, [editMode])
    
    // for public users, only show confirmed data
    const filteredConfirmationStatuses = confirmationStatuses?.filter(
        (confirmationStatus) => {
            if (publicContext) {
                return confirmationStatus?.code === 'confirmed';
            }
    
            return confirmationStatus?.code !== 'on-hold';
    });

    // only show benefits for which we have modeled uplift
    // within the confirmationStatuses that are visible.
    const filteredEnvironmentalBenefits = useFilteredBenefits(benefitSummary);
    
    const environmentalBenefit = filteredEnvironmentalBenefits?.find(
        (x) => String(x?.environmentalBenefitId) === String(selectedEnvironmentalBenefitId)
    )
    
    // ensure our selected benefit is a valid selection, otherwise use the first available
    useEffect(() => {
        if(!environmentalBenefit && filteredEnvironmentalBenefits?.length > 0) {
            setSelectedEnvironmentalBenefitId(filteredEnvironmentalBenefits[0]?.environmentalBenefitId ?? "");
        }
    }, [filteredEnvironmentalBenefits, environmentalBenefit]);
    
    if(!loaded || editMode || !confirmationStatuses || !environmentalBenefits) {
        return null;
    }
    
    return <Fragment>
        <h3 className="table-headers">Benefit Summary</h3>

        <Conditional Condition={filteredEnvironmentalBenefits?.length > 0}>
            <EnvironmentalBenefitFilter
                EnvironmentalBenefits={filteredEnvironmentalBenefits}
                Value={selectedEnvironmentalBenefitId ?? ""}
                UpdateValue={setSelectedEnvironmentalBenefitId}
                HideUnselectedOption={true}
            />
        </Conditional>
        <ContextNodeBenefitSummary
            parentContextNodeId={contextNodeId}
            benefitSummary={benefitSummary}
            environmentalBenefitId={environmentalBenefit?.environmentalBenefitId}
            confirmationStatuses={filteredConfirmationStatuses}
        />
    </Fragment>

}
